import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { LuExternalLink } from 'react-icons/lu';
import type { AgentApiCandidateFormsShow } from '@/types/serializers';
import Input from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Button } from '@/components/Button';
import useUpdateCandidateFormThanks from '@/features/agent/form/api/useUpdateCandidateFormThanks';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import {
  candidateFormThanksSchema,
  type CandidateFormThanksSchema,
} from '@/features/agent/form/schemas/candidateFormThanksSchema';

const THANKS_TITLE_MAX_LENGTH = 30;
const THANKS_MESSAGE_MAX_LENGTH = 1000;

const DEFAULT_MESSAGE_PLACEHOLDER = `フォームの送信を完了致しました。
1~2営業日以内にご連絡させていただきます。`;

interface Props {
  candidateForm: AgentApiCandidateFormsShow;
}

export default function FormThanksTab({ candidateForm }: Props) {
  const { mutateAsync: updateThanks, isPending: isUpdating } = useUpdateCandidateFormThanks();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CandidateFormThanksSchema>({
    resolver: zodResolver(candidateFormThanksSchema),
    mode: 'onSubmit', // フォーム送信時にバリデーションを実行
    reValidateMode: 'onChange', // バリデーション後は変更時に再検証
    defaultValues: {
      thanks_title: candidateForm.thanks_title ?? null,
      thanks_message: candidateForm.thanks_message ?? null,
      header_tag: candidateForm.header_tag ?? null,
    },
  });

  useEffect(() => {
    setValue('thanks_title', candidateForm.thanks_title ?? null);
    setValue('thanks_message', candidateForm.thanks_message ?? null);
    setValue('header_tag', candidateForm.header_tag ?? null);
  }, [
    candidateForm.thanks_title,
    candidateForm.thanks_message,
    candidateForm.header_tag,
    setValue,
  ]);

  const onSubmit = async (data: CandidateFormThanksSchema) => {
    try {
      await updateThanks({
        thanks_title: data.thanks_title || null,
        thanks_message: data.thanks_message || null,
        header_tag: data.header_tag || null,
      });
      showSuccessToast('保存しました');
    } catch (err) {
      const message = err instanceof Error ? err.message : '保存に失敗しました';
      showErrorToast(message);
    }
  };

  const thanksPageUrl = `/forms/${candidateForm.encrypt_key}/thanks`;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-xl">
      <div className="flex flex-col gap-md rounded-sm bg-white p-xl shadow-layer1">
        <div className="flex flex-col gap-xs">
          <label htmlFor="thanks-title" className="text-heading-xxs text-main">
            サンクスページのタイトル
          </label>
          <div className="flex flex-col gap-xxs">
            <Input
              id="thanks-title"
              type="text"
              {...register('thanks_title', {
                onChange: (e) => {
                  const value = e.target.value.slice(0, THANKS_TITLE_MAX_LENGTH);
                  setValue('thanks_title', value || null, { shouldValidate: true });
                },
              })}
              placeholder="送信完了ポスト"
              className="w-full"
              maxLength={THANKS_TITLE_MAX_LENGTH}
            />
            {errors.thanks_title && (
              <p className="text-body-xs text-danger">{errors.thanks_title.message}</p>
            )}
            <p className="text-body-xs text-sub">{THANKS_TITLE_MAX_LENGTH}文字以内</p>
          </div>
        </div>

        <div className="flex flex-col gap-xs">
          <label htmlFor="thanks-message" className="text-heading-xxs text-main">
            サンクスページのメッセージ
          </label>
          <Textarea
            id="thanks-message"
            {...register('thanks_message', {
              onChange: (e) => {
                const value = e.target.value.slice(0, THANKS_MESSAGE_MAX_LENGTH);
                setValue('thanks_message', value || null, { shouldValidate: true });
              },
            })}
            placeholder={DEFAULT_MESSAGE_PLACEHOLDER}
            className="min-h-[120px] w-full resize-y"
            maxLength={THANKS_MESSAGE_MAX_LENGTH}
            rows={5}
            error={errors.thanks_message?.message}
          />
          <p className="text-body-xs text-sub">
            {THANKS_MESSAGE_MAX_LENGTH.toLocaleString()}文字以内
          </p>
        </div>

        <div className="flex flex-col gap-xs">
          <label htmlFor="header-tag" className="text-heading-xxs text-main">
            ヘッダータグ
          </label>
          <Textarea
            id="header-tag"
            {...register('header_tag', {
              onChange: (e) => {
                setValue('header_tag', e.target.value || null, { shouldValidate: true });
              },
            })}
            placeholder="<script>...</script>"
            className="min-h-[100px] w-full resize-y font-mono"
            rows={4}
            error={errors.header_tag?.message}
          />
          <p className="text-body-xs text-sub">
            フォームからサンクスページのヘッダーに差し込まれます
          </p>
        </div>

        <div className="mt-md flex items-center justify-between">
          <Button asChild color="secondary" size="md">
            <Link to={thanksPageUrl} target="_blank" rel="noopener noreferrer">
              サンクスページプレビュー
              <LuExternalLink className="size-4" />
            </Link>
          </Button>
          <Button
            type="submit"
            color="primary"
            size="md"
            disabled={isUpdating || Object.keys(errors).length > 0}
          >
            保存する
          </Button>
        </div>
      </div>
    </form>
  );
}
